if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      /** Debounce + throttle variant requests to avoid 429 (aggressive to prevent rate limit) */
      _variantChangeDebounceMs = 500;
      _variantChangeMinIntervalMs = 1100;
      _variantChangeDebounceTimer = null;
      _variantChangeThrottleTimer = null;
      _pendingVariantChange = null;
      _lastVariantRequestTime = 0;
      _lastVariantRequestCacheKey = null;
      _variantChange429RetryTimer = null;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        document.addEventListener('availability:updated', (e) => {
          const { data } = e.detail || {};
          if (data) {
            this.updateMaxFromAvailability(data);
          }
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        if (this._variantChangeDebounceTimer) clearTimeout(this._variantChangeDebounceTimer);
        if (this._variantChangeThrottleTimer) clearTimeout(this._variantChangeThrottleTimer);
        if (this._variantChange429RetryTimer) clearTimeout(this._variantChange429RetryTimer);
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // Check client-side if the selected options match an existing variant.
        // If no variant exists for this combination, show Unavailable immediately
        // without fetching from the server (which would reset the user's selection).
        const userOptions = this.getSelectedOptionValueStrings();
        const { variant: exactVariant } = this.resolveVariantForOptionValues(userOptions);
        if (!exactVariant) {
          this.abortController?.abort();
          if (this._variantChangeDebounceTimer) clearTimeout(this._variantChangeDebounceTimer);
          this._pendingVariantChange = null;
          this.setUnavailable();
          return;
        }

        this.resetProductFormState();
        this.abortController?.abort();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // Debounce section requests so rapid variant toggles don't hit 429 (Too Many Requests)
        this._pendingVariantChange = {
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        };

        if (this._variantChangeDebounceTimer) clearTimeout(this._variantChangeDebounceTimer);
        this._variantChangeDebounceTimer = setTimeout(() => {
          this._variantChangeDebounceTimer = null;
          const pending = this._pendingVariantChange;
          this._pendingVariantChange = null;
          if (pending) this._runVariantRequestWhenReady(pending);
        }, this._variantChangeDebounceMs);
      }

      /** Throttle: run request now or after min interval so we never flood the server (avoids 429). */
      _runVariantRequestWhenReady(pending) {
        const cacheKey = this.getSectionCacheKey(pending.requestUrl);
        if (cacheKey === this._lastVariantRequestCacheKey) return;
        const now = Date.now();
        const elapsed = now - this._lastVariantRequestTime;
        const wait = this._variantChangeMinIntervalMs - elapsed;
        if (this._lastVariantRequestTime > 0 && wait > 0) {
          if (this._variantChangeThrottleTimer) clearTimeout(this._variantChangeThrottleTimer);
          this._variantChangeThrottleTimer = setTimeout(() => {
            this._variantChangeThrottleTimer = null;
            this._runVariantRequestWhenReady(pending);
          }, wait);
          return;
        }
        this._lastVariantRequestCacheKey = cacheKey;
        this.renderProductInfo({
          requestUrl: pending.requestUrl,
          targetId: pending.targetId,
          callback: pending.callback,
        });
      }

      /** Returns [option1, option2, option3] from the current variant-selects form state (radios and selects in DOM order). */
      getSelectedOptionValueStrings() {
        const vs = this.variantSelectors;
        if (!vs) return [];
        const values = [];
        const groups = vs.querySelectorAll('.product-form__input--swatch, .product-form__input--pill, .product-form__input--dropdown');
        groups.forEach((group, index) => {
          const checked = group.querySelector('input[type="radio"]:checked');
          const select = group.querySelector('select.select__select');
          if (checked) values[index] = checked.value || null;
          else if (select) values[index] = select.value || null;
        });
        return values;
      }

      /** Normalize option value for comparison (e.g. "Black | Black" vs "Black"). */
      normalizeOptionValue(val) {
        if (val == null || val === '') return '';
        const s = String(val).trim();
        return s.includes('|') ? s.split('|')[0].trim() : s;
      }

      /**
       * Find a variant matching the given option value strings. If exact match exists (in stock or out of stock),
       * use it so the user can land on that variant's product page. Only when no variant exists for the selection
       * do we return a fallback (same color/size from left), preferring available.
       */
      resolveVariantForOptionValues(optionValues) {
        const variants = window.productVariants;
        if (!variants?.length) return { variant: null, fallbackVariant: null };

        const exact = variants.find((v) => {
          const o1 = this.normalizeOptionValue(v.option1);
          const o2 = this.normalizeOptionValue(v.option2);
          const o3 = this.normalizeOptionValue(v.option3);
          const a1 = this.normalizeOptionValue(optionValues[0]);
          const a2 = this.normalizeOptionValue(optionValues[1]);
          const a3 = this.normalizeOptionValue(optionValues[2]);
          return o1 === a1 && o2 === a2 && o3 === a3;
        });

        // Use exact match whenever it exists so the user can go to that variant's page (including out of stock).
        if (exact) return { variant: exact, fallbackVariant: null };

        // No variant exists for this selection: pick a fallback that does exist (same color, etc.), preferring available.
        for (let len = 1; len <= 3; len++) {
          const matching = variants.filter((v) => {
            for (let i = 0; i < len; i++) {
              const vVal = this.normalizeOptionValue(v[`option${i + 1}`]);
              const aVal = this.normalizeOptionValue(optionValues[i]);
              if (vVal !== aVal) return false;
            }
            return true;
          });
          const availableMatch = matching.find((v) => v.available);
          const fallback = availableMatch || matching[0];
          if (fallback) return { variant: null, fallbackVariant: fallback };
        }

        return { variant: null, fallbackVariant: null };
      }

      /** Set variant-selects form to the given variant (radios and selects) without dispatching change. */
      syncFormToVariant(variant) {
        const vs = this.variantSelectors;
        if (!vs || !variant) return;
        const optionValues = [variant.option1, variant.option2, variant.option3].filter((o) => o != null);
        const groups = vs.querySelectorAll('.product-form__input--swatch, .product-form__input--pill, .product-form__input--dropdown');
        const targetVal = (v) => this.normalizeOptionValue(v);
        optionValues.forEach((optionValue, index) => {
          if (optionValue == null || optionValue === '') return;
          const group = groups[index];
          if (!group) return;
          const optionNorm = targetVal(optionValue);
          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
            (input) => targetVal(input.value) === optionNorm
          );
          if (radio) {
            radio.checked = true;
            if (vs.updateSelectionMetadata) vs.updateSelectionMetadata({ target: radio });
            return;
          }
          const select = group.querySelector('select.select__select');
          if (select?.options) {
            for (let i = 0; i < select.options.length; i++) {
              if (targetVal(select.options[i].value) === optionNorm) {
                select.selectedIndex = i;
                if (vs.updateSelectionMetadata) vs.updateSelectionMetadata({ target: select });
                break;
              }
            }
          }
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        // Keep add-to-cart button gray (not red) during variant switch to avoid visual glitch
        this.querySelectorAll('[type="submit"][data-sold-out-message]').forEach((btn) => {
          btn.classList.add('button--variant-loading');
        });
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, _retryAfter429 = false }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const cacheKey = this.getSectionCacheKey(requestUrl);
        const cached = this.prefetchCache?.get(cacheKey);
        const now = Date.now();
        if (cached && (now - cached.at) < (this.sectionCacheTTL ?? 20000)) {
          this.pendingRequestUrl = null;
          callback(cached.html);
          document.querySelector(`#${targetId}`)?.focus();
          return;
        }

        this._lastVariantRequestTime = Date.now();
        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (response.status === 429 && !_retryAfter429) {
              return Promise.reject({ status: 429, retry: true });
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.setSectionCache(cacheKey, html);
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              return;
            }
            if (error.status === 429 && error.retry) {
              if (this._variantChange429RetryTimer) clearTimeout(this._variantChange429RetryTimer);
              const retryDelay = 2500;
              this._variantChange429RetryTimer = setTimeout(() => {
                this._variantChange429RetryTimer = null;
                this.renderProductInfo({
                  requestUrl,
                  targetId,
                  callback,
                  _retryAfter429: true,
                });
              }, retryDelay);
              return;
            }
            console.error(error);
          });
      }

      getSectionCacheKey(url) {
        return url.replace(/([?&])_t=\d+/g, '').replace(/\?&/, '?').replace(/[&?]$/, '') || url;
      }

      setSectionCache(cacheKey, html) {
        if (!this.prefetchCache) this.prefetchCache = new Map();
        this.prefetchCache.set(cacheKey, { html, at: Date.now() });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        params.push(`_t=${Date.now()}`);

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);

          if (!variant) {
            this.updateVariantInputs();
            this.setUnavailable();
            return;
          }

          // Delivery stays initialized and only switches between its normal and
          // seasonal views. Other guarded regions update only at a state boundary.
          this.querySelectorAll('[data-seasonal-region][data-seasonal-block-id]').forEach((destination) => {
            const blockId = CSS.escape(destination.dataset.seasonalBlockId);
            const region = CSS.escape(destination.dataset.seasonalRegion);
            const source = html.querySelector(
              `[data-seasonal-region="${region}"][data-seasonal-block-id="${blockId}"]`
            );
            if (!source) return;

            const nextIsOffSeason = source.dataset.seasonalOffSeason === 'true';
            if (destination.dataset.seasonalRegion === 'delivery') {
              const notice = destination.querySelector('[data-seasonal-view="notice"]');
              const options = destination.querySelector('[data-seasonal-view="options"]');
              notice?.classList.toggle('hidden', !nextIsOffSeason);
              options?.classList.toggle('hidden', nextIsOffSeason);
            } else if (source.dataset.seasonalOffSeason !== destination.dataset.seasonalOffSeason) {
              HTMLUpdateUtility.setInnerHTML(destination, source.innerHTML);
            }

            destination.dataset.seasonalOffSeason = source.dataset.seasonalOffSeason;
          });

          this.updateVariantInputs(variant.id);
          this.pickupAvailability?.update(variant);

          this.updateMedia(html, variant?.featured_media?.id, variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));

              Array.from(source.attributes).forEach((attr) => {
                if (attr.name.startsWith('data-')) {
                  destination.setAttribute(attr.name, attr.value);
                }
              });
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('BuyButtons');
          updateSourceFromDestination('stock-bar-container');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // Update submit button state based on variant availability,
          // but keep preorder products purchasable on PDP.
          const isPreorderEnabled = this.querySelector('product-form[data-preorder-enabled="true"]') !== null;
          const isDisabled = !variant.available && !isPreorderEnabled;
          this.productForm?.toggleSubmitButton(isDisabled, isDisabled ? window.variantStrings.soldOut : undefined);

          // Update Buy It Now / Email When Available buttons
          const buyItNowBtn = this.querySelector('.button--buy-now, [id^="BuyItNow-"]');
          let emailBtn = this.querySelector('.button--email-when-available, [id^="EmailWhenAvailable-"]');
          const bisWrapper = this.querySelector('.bis-wrapper');
          if (isDisabled) {
            // Out of stock: hide Buy It Now, show Email When Available
            if (buyItNowBtn) buyItNowBtn.style.display = 'none';
            if (emailBtn) {
              emailBtn.style.display = '';
              emailBtn.setAttribute('data-variant-id', variant.id);
              // Reset state if variant changed
              emailBtn.classList.remove('bis-success');
              emailBtn.disabled = false;
              emailBtn.innerHTML = '<span class="button__text"> email me when available </span>';
              const oldForm = (bisWrapper || emailBtn.parentElement).querySelector('.bis-form');
              if (oldForm) oldForm.remove();
            } else if (buyItNowBtn) {
              // Create notify button wrapped in bis-wrapper
              const wrapper = document.createElement('div');
              wrapper.className = 'bis-wrapper';
              const notifyBtn = document.createElement('button');
              notifyBtn.type = 'button';
              notifyBtn.className = 'button button--email-when-available';
              notifyBtn.style.paddingLeft = '2rem';
              notifyBtn.style.paddingRight = '2rem';
              notifyBtn.setAttribute('data-variant-id', variant.id);
              notifyBtn.innerHTML = '<span class="button__text"> email me when available </span>';
              wrapper.appendChild(notifyBtn);
              buyItNowBtn.parentNode.insertBefore(wrapper, buyItNowBtn.nextSibling);
              buyItNowBtn.style.display = 'none';
            }
            if (bisWrapper) bisWrapper.style.display = '';
          } else {
            // Available: show Buy It Now, hide Email When Available
            if (buyItNowBtn) {
              buyItNowBtn.style.display = '';
              buyItNowBtn.removeAttribute('disabled');
            }
            if (emailBtn) emailBtn.style.display = 'none';
            if (bisWrapper) bisWrapper.style.display = 'none';
          }

          // Also update any quick-add submit buttons directly, preserving icons and text
          this.querySelectorAll('[type="submit"][data-sold-out-message]').forEach((btn) => {
            btn.classList.remove('button--variant-loading');
            const btnInResponse = html.querySelector(`#${btn.id}`);
            if (btnInResponse) {
              const spanContent = btnInResponse.querySelector('span:first-child');
              const currentSpan = btn.querySelector('span:first-child');
              if (spanContent && currentSpan) {
                currentSpan.innerHTML = spanContent.innerHTML;
              }
            }
            if (isDisabled) {
              btn.setAttribute('disabled', 'disabled');
            } else {
              btn.removeAttribute('disabled');
            }
          });
          
          // Update quantity input disabled state
          this.querySelectorAll('quantity-input').forEach((qi) => {
            if (isDisabled) {
              qi.classList.add('quantity-disabled');
              qi.querySelector('.quantity__input')?.setAttribute('disabled', 'disabled');
            } else {
              qi.classList.remove('quantity-disabled');
              qi.querySelector('.quantity__input')?.removeAttribute('disabled');
            }
          });

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        // Update variant inputs in all forms within this product-info
        this.querySelectorAll('input[name="id"].product-variant-id, form[data-type="add-to-cart-form"] input[name="id"]').forEach((input) => {
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        // Also update any standard product forms
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input && input.value !== String(variantId ?? '')) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        
        // Dispatch custom event for delivery-options and other listeners
        document.dispatchEvent(new CustomEvent('variant:changed', {
          detail: { variantId, sectionId: this.dataset.section },
          bubbles: true
        }));
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.querySelectorAll('[type="submit"][data-sold-out-message]').forEach((btn) => {
          btn.classList.remove('button--variant-loading');
        });
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        // Disable Buy It Now and hide Email When Available (non-existing variant)
        const buyItNowBtn = this.querySelector('.button--buy-now, [id^="BuyItNow-"]');
        const emailBtn = this.querySelector('.button--email-when-available, [id^="EmailWhenAvailable-"]');
        const bisWrapper = this.querySelector('.bis-wrapper');
        if (buyItNowBtn) {
          buyItNowBtn.style.display = '';
          buyItNowBtn.setAttribute('disabled', 'disabled');
        }
        if (emailBtn) emailBtn.style.display = 'none';
        if (bisWrapper) bisWrapper.style.display = 'none';

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, variant) {
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const responseProductInfo = html.querySelector('product-info');
        const mediaGalleryDestination = responseProductInfo?.querySelector('media-gallery ul') || html.querySelector('media-gallery ul');
        if (!mediaGallerySource || !mediaGalleryDestination) return;

        const destinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
        const firstDestinationMediaId = destinationItems[0]?.dataset?.mediaId;
        const activeMediaId = variantFeaturedMediaId
          ? `${this.dataset.section}-${variantFeaturedMediaId}`
          : firstDestinationMediaId || null;
        if (!activeMediaId) return;

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM (clone so cached HTML is not mutated)
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i].cloneNode(true));
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(activeMediaId, true);

        // update media modal (scope to response product-info when present)
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent =
          responseProductInfo?.querySelector('product-modal .product-media-modal__content') ||
          html.querySelector('product-modal .product-media-modal__content');
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : Math.max(data.max - data.cartQuantity, 0);
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
        min = Math.max(min, 1);

        this.quantityInput.min = min;

        if (max && max >= 1) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`) || html.querySelector('.product-form__quantity');
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step', 'disabled', 'min', 'value'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      // Listen for centralized availability updates to set max quantity from Storefront API
      updateMaxFromAvailability(data) {
        if (!this.quantityInput || !data) return;

        // Use effective availability quantity based on selected location
        const maxQuantity = data.effectiveAvailability?.quantity;
        if (maxQuantity !== undefined && maxQuantity !== null) {
          this.quantityInput.setAttribute('data-max', maxQuantity);
          this.setQuantityBoundries();
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
